import { Stepper, StepIconProps, Grow } from '@mui/material';
import { forwardRef } from 'react';
import { StyledStep, StyledStepConnector, StyledStepIconRoot, StyledStepLabel } from './styles';

export interface WizardStep {
  label: string;
  description?: string;
  /** Custom icon to display in the default state */
  icon?: React.ReactNode;
  /** Custom icon to display when step is active */
  activeIcon?: React.ReactNode;
  /** Custom icon to display when step is completed */
  completedIcon?: React.ReactNode;
}

interface WizardStepperProps {
  /** Array of step objects with label and optional description */
  steps: WizardStep[];
  /** Current active step (0-indexed) */
  activeStep: number;
  /** Orientation of the stepper */
  orientation?: 'horizontal' | 'vertical';
  /** Whether to use alternative label placement (below icon) */
  alternativeLabel?: boolean;
  /** Callback when a step is clicked */
  onStepClick?: (stepIndex: number) => void;
  /** Whether steps are clickable. If true, only previous steps will be clickable */
  clickable?: boolean;
  /** Custom connector component */
  connector?: React.ReactElement<unknown, string> | null;
  /** Whether to show step descriptions */
  showDescriptions?: boolean;
  /** Use custom icons from steps instead of numbers */
  useIcons?: boolean;
}

interface CustomStepIconProps extends StepIconProps {
  customIcon?: React.ReactNode;
  activeIcon?: React.ReactNode;
  completedIcon?: React.ReactNode;
  useIcons?: boolean;
}

const StepIcon = (props: CustomStepIconProps) => {
  const { active, completed, icon, customIcon, activeIcon, completedIcon, useIcons } = props;

  const renderContent = () => {
    if (completed && useIcons && completedIcon) {
      return <span className="step-icon-completed">{completedIcon}</span>;
    }
    if (completed) {
      return <span className="step-icon-check">✓</span>;
    }
    if (active && useIcons && activeIcon) {
      return <span className="step-icon-active">{activeIcon}</span>;
    }
    if (useIcons && customIcon) {
      return <span className="step-icon-custom">{customIcon}</span>;
    }
    return <span className="step-icon-number">{icon}</span>;
  };

  return (
    <StyledStepIconRoot ownerState={{ active, completed }}>
      <Grow in={true} timeout={500}>
        {renderContent()}
      </Grow>
    </StyledStepIconRoot>
  );
};
export const WizardStepper = forwardRef<HTMLDivElement, WizardStepperProps>(
  (
    {
      steps,
      activeStep,
      orientation = 'horizontal',
      alternativeLabel = false,
      onStepClick,
      clickable = false,
      connector,
      useIcons = false,
    },
    ref
  ) => {
    const handleStepClick = (index: number) => {
      // Only allow clicking on previous steps
      if (clickable && onStepClick && index < activeStep) {
        onStepClick(index);
      }
    };

    return (
      <Stepper
        ref={ref}
        activeStep={activeStep}
        orientation={orientation}
        alternativeLabel={alternativeLabel}
        connector={connector ?? <StyledStepConnector />}
      >
        {steps.map((step, index) => (
          <Grow key={step.label} in timeout={500}>
            <StyledStep
              completed={index < activeStep}
              active={index === activeStep}
              onClick={() => handleStepClick(index)}
              sx={{
                cursor: clickable && index < activeStep ? 'pointer' : 'default',
              }}
            >
              <StyledStepLabel
                slots={{ stepIcon: StepIcon }}
                slotProps={{
                  stepIcon: {
                    customIcon: step.icon,
                    activeIcon: step.activeIcon,
                    completedIcon: step.completedIcon,
                    useIcons,
                  } as CustomStepIconProps,
                }}
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: clickable && index < activeStep ? 'pointer' : 'default',
                }}
              >
                <span className="step-label-content">
                  <span
                    className={`step-number ${index === activeStep ? 'active' : ''} ${
                      index < activeStep ? 'completed' : ''
                    }`}
                  >
                    Step {index + 1}
                  </span>
                  <span
                    className={`step-title ${index === activeStep ? 'active' : ''} ${
                      index < activeStep ? 'completed' : ''
                    }`}
                  >
                    {step.label}
                  </span>
                </span>
              </StyledStepLabel>
            </StyledStep>
          </Grow>
        ))}
      </Stepper>
    );
  }
);

// For TypeScript type checking with forwardRef
WizardStepper.displayName = 'WizardStepper';
